import { CachePolicies, useFetch } from 'use-http';
import useAsyncEffect from 'use-async-effect';
import React, { useContext } from 'react';
import { routes } from '@/webapi/routes';
import { useComplexState } from '@/utils/use-complex-state';
import { AccountContext } from '@/features/account-context';
import { useStoreSettingsApi } from '@/webapi/use-store-settings-api';

type STATUS = 'YES' | 'NO' | 'UNSET';

export type IntegrationsStatus = {
  klavio: STATUS;
  klavyioSegments: STATUS;
  googleAnalytics: STATUS;
  facebook: STATUS;
  instagram: STATUS;
  klavyioSegmentsList?: Array<{ name: string; id: string }>;

  trackingIntegrations?: {
    amplitude: STATUS;
    clarity: STATUS;
    contentsquare: STATUS;
    fullstory: STATUS;
    heatmap: STATUS;
    hotjar: STATUS;
    segment: STATUS;
  };
};

export const IntegrationsContext = React.createContext(
  {} as IntegrationsContext,
);

type IntegrationsContext = {
  toggleGoogleAnalytics: () => Promise<void>;
  loading: boolean;
  toggleKlavio: () => Promise<void>;
  toggleKlavyioSegments: () => Promise<void>;
  toggleFacebook: () => Promise<void>;
  toggleInstagram: () => Promise<void>;
  toggleTrackingIntegration: (integration: string) => Promise<void>;
  integrationsStatus: IntegrationsStatus;
  ga4SendEventsByDefault: boolean;
  toggleGa4SendEventsByDefault: (selected: boolean) => Promise<void>;
};

export function useGetIntegrationStatus(initialLoading?: boolean) {
  const { get, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: initialLoading,
    },
  }));

  const getIntegrationStatus = async () =>
    (await get(routes.integrations)) as IntegrationsStatus;
  return {
    loading,
    getIntegrationStatus,
  };
}

export function newIntegrationsContext(
  initialLoading?: boolean,
): IntegrationsContext {
  const { get, put, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: initialLoading,
    },
  }));

  const { generalStoreSettings, setGeneralStoreSettings } =
    useContext(AccountContext);
  const { setGeneralStoreSettings: saveGeneralStoreSettings } =
    useStoreSettingsApi();

  const [integrationsStatus, setIntegrationsStatus] =
    useComplexState<IntegrationsStatus>({} as IntegrationsStatus);

  const ga4SendEventsByDefault =
    generalStoreSettings?.ga4SendEventsByDefault ?? false;

  const setStatus = async () => {
    const resp = (await get(routes.integrations)) as IntegrationsStatus;
    setIntegrationsStatus((draft) => {
      draft.klavio = resp?.klavio || `UNSET`;
      draft.klavyioSegments = resp?.klavyioSegments || `UNSET`;
      draft.facebook = resp?.facebook || `UNSET`;
      draft.googleAnalytics = resp?.googleAnalytics || `UNSET`;
      draft.instagram = resp?.instagram || `UNSET`;
      draft.trackingIntegrations = resp?.trackingIntegrations;
    });
  };

  useAsyncEffect(async () => {
    await setStatus();
  }, []);

  const toggleKlavio = async () => {
    const value = integrationsStatus?.klavio === `YES` ? `NO` : `YES`;
    setIntegrationsStatus((draft) => {
      draft.klavio = value;
    });
    await put(routes.integrations, {
      klavio: value,
    });
  };

  const klavyioDisconnect = async () => {
    const value = `NO`;
    setIntegrationsStatus((draft) => {
      draft.klavyioSegments = value;
    });
    await get(routes.klavyioDisconnect);
  };

  const toggleGoogleAnalytics = async () => {
    const value = integrationsStatus?.googleAnalytics === `YES` ? `NO` : `YES`;
    setIntegrationsStatus((draft) => {
      draft.googleAnalytics = value;
    });
    await put(routes.integrations, { googleAnalytics: value });
    const newGa4Default = value === `YES`;
    setGeneralStoreSettings?.({
      ...generalStoreSettings,
      ga4SendEventsByDefault: newGa4Default,
    });
    await saveGeneralStoreSettings({ ga4SendEventsByDefault: newGa4Default });
  };

  const toggleFacebook = async () => {
    const value = integrationsStatus?.facebook === `YES` ? `NO` : `YES`;
    setIntegrationsStatus((draft) => {
      draft.facebook = value;
    });
    await put(routes.integrations, {
      facebook: value,
    });
  };

  const toggleInstagram = async () => {
    const value = integrationsStatus?.instagram === `YES` ? `NO` : `YES`;
    setIntegrationsStatus((draft) => {
      draft.instagram = value;
    });
    await put(routes.integrations, {
      instagram: value,
    });
  };

  const toggleTrackingIntegration = async (integration: string) => {
    const value =
      integrationsStatus?.trackingIntegrations?.[integration] === `YES`
        ? `NO`
        : `YES`;
    setIntegrationsStatus((draft) => {
      draft.trackingIntegrations = {
        ...draft.trackingIntegrations,
        [integration]: value,
      };
    });
    const integrations = await put(routes.integrations, {
      trackingIntegrations: {
        ...integrationsStatus.trackingIntegrations,
        [integration]: value,
      },
    });
    setIntegrationsStatus((draft) => {
      draft.trackingIntegrations = integrations?.trackingIntegrations;
    });
  };

  const toggleGa4SendEventsByDefault = async (selected: boolean) => {
    setGeneralStoreSettings?.({
      ...generalStoreSettings,
      ga4SendEventsByDefault: selected,
    });
    await saveGeneralStoreSettings({ ga4SendEventsByDefault: selected });
  };

  return {
    loading,
    toggleTrackingIntegration,
    integrationsStatus,
    toggleInstagram,
    toggleKlavyioSegments: klavyioDisconnect,
    toggleKlavio,
    toggleGoogleAnalytics,
    toggleFacebook,
    ga4SendEventsByDefault,
    toggleGa4SendEventsByDefault,
  };
}
